import { ReactNode, useEffect, useMemo, useState } from 'react';
import { EmptyStates } from '@frontend/components';
import { ErrorBoundary } from '@frontend/error-boundary';
import { useTranslation } from '@frontend/i18n';
import {
  AverageWaitChart,
  AnswerRateChart,
  TimeRangeFilter,
  TimeRangeChangeValue,
  defaultTimeRanges,
} from '@frontend/phone-charts';
import { useAppScopeStore, WeaveLocation } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, DropdownChip, Text } from '@frontend/design-system';
import { ChartDataLayer } from './analytics-data-layer';
import type { AnalyticChartsData } from './types';

/**
 * This function returns a concise list of locations with phoneTenantId.
 *
 * Databeta and single locations will be returned as-is, while unify locations will return only the parent location.
 */
const getTenantLocations = (locations: WeaveLocation[]) => {
  return locations.filter((location) =>
    location.locationType === 'unify' ? location.phoneTenantId && !location.groupId : location.phoneTenantId
  );
};

export function AnalyticsView() {
  const { selectedLocationIds, forcedGroupId, accessibleLocationData, groupData } = useAppScopeStore();
  // const [selectedOverTimeCallQueue, setSelectedOverTimeCallQueue] = useState<{ id: string; label: string } | undefined>(
  //   undefined
  // );

  /**
   * These options are TENANTS, not locations. This means that it's the parent location for unify locations, and the location itself for other types.
   */
  const options = useMemo(() => {
    return getTenantLocations(
      [...new Set([...selectedLocationIds, forcedGroupId])].map(
        (locationId) => accessibleLocationData[locationId] ?? groupData[locationId]
      )
    ).map((location) => ({
      id: location.locationId ?? '',
      label: location.name ?? '',
    }));
  }, [accessibleLocationData, forcedGroupId, groupData, selectedLocationIds]);

  const [selectedLocationOption, setSelectedLocationOption] = useState<{ id: string; label: string }>(
    options.length > 0 ? options[0] : { id: '', label: '' }
  );
  const [selectedTimeOption, setSelectedTimeOption] = useState<TimeRangeChangeValue>(() => {
    const range = defaultTimeRanges[0];
    const [startDate, endDate] = range.getDateRange();

    return {
      definition: range.definition,
      label: range.label,
      startDate,
      endDate,
    };
  });

  /**
   * This effect is to reset the selected location option when the options change, which happens when switching locations in the global picker.
   *
   * This should only be necessary for users with access to multiple orgs, including weave type users.
   */
  useEffect(() => {
    if (options.length > 0) {
      setSelectedLocationOption(options[0]);
    }
  }, [options]);

  const callQueueTenantBase =
    accessibleLocationData[selectedLocationOption.id]?.phoneTenantId ??
    groupData[selectedLocationOption.id]?.phoneTenantId ??
    '';

  return (
    <div style={{ height: '100%', display: 'grid', gap: theme.spacing(2) }}>
      <div style={{ display: 'grid', gap: theme.spacing(1), gridTemplateColumns: 'max-content max-content' }}>
        {options.length > 1 && (
          <DropdownChip
            options={options}
            selectedOption={selectedLocationOption}
            onSelectOption={(opt) => {
              setSelectedLocationOption(opt);
            }}
            iconName='locations-small'
          />
        )}
        <TimeRangeFilter css={{ width: 'fit-content' }} onChange={setSelectedTimeOption} />
      </div>
      <ErrorBoundary tier='feature' feature='calls-v2.analytics-view' team='fe-phones' fallback={Boundary}>
        <ChartDataLayer
          tenantId={callQueueTenantBase}
          locationIds={selectedLocationIds}
          selectedTimeOption={selectedTimeOption}
          // selectedOverTimeCallQueueId={selectedOverTimeCallQueue?.id ?? callQueues[0]?.callQueueId}
          render={(props) => (
            <AnalyticCharts
              {...props}
              selectedLocationOption={selectedLocationOption}
              selectedTimeOption={selectedTimeOption}
              // selectedOverTimeCallQueue={
              //   selectedOverTimeCallQueue ?? {
              //     id: callQueues[0]?.callQueueId,
              //     label: callQueues[0]?.name,
              //   }
              // }
              // setSelectedOverTimeCallQueue={setSelectedOverTimeCallQueue}
            />
          )}
        />
      </ErrorBoundary>
    </div>
  );
}

function AnalyticCharts({
  data,
  isLoading,
  isUsingCdr2Data,
  selectedTimeOption,
  selectedLocationOption,
}: {
  data?: AnalyticChartsData;
  isLoading: boolean;
  isUsingCdr2Data?: boolean;
  selectedTimeOption: TimeRangeChangeValue;
  selectedLocationOption: { id: string; label: string };
  // selectedOverTimeCallQueue: { id: string; label: string };
  // setSelectedOverTimeCallQueue: (item: { id: string; label: string }) => void;
}) {
  const { t } = useTranslation('phone');

  if (!data) {
    return (
      <EmptyContainer>
        <EmptyStates emptyStateConfig={{ type: 'phone_extensions', header: t('No data to display') }} />
      </EmptyContainer>
    );
  }

  const { averageWait, answerRate } = data;

  return (
    <div style={{ minWidth: 0 }}>
      <AverageWaitChart
        isLoading={isLoading}
        data={averageWait.base}
        pastData={averageWait.candidate}
        groupLabels={averageWait.groupLabels}
        dateLabel={selectedTimeOption.label}
        locationLabel={selectedLocationOption.label}
        timeRangeDefinition={selectedTimeOption.definition}
      />
      <AnswerRateChart
        isLoading={isLoading}
        data={answerRate.base}
        pastData={answerRate.candidate}
        groupLabels={answerRate.groupLabels}
        dateLabel={selectedTimeOption.label}
        locationLabel={selectedLocationOption.label}
        timeRangeDefinition={selectedTimeOption.definition}
        isUsingCdr2Data={isUsingCdr2Data}
      />
      {/* Not doing this for now */}
      {/* <AverageWaitOverTimeChart
        data={averageWaitOverTime.base}
        groupLabels={averageWaitOverTime.groupLabels}
        dateLabel={selectedTimeOption.label}
        focusOptions={Object.entries(callQueueRecord).map(([id, label]) => ({ id, label }))}
        setSelectedFocusOption={setSelectedOverTimeCallQueue}
        selectedFocusOption={selectedOverTimeCallQueue}
      /> */}
    </div>
  );
}

function EmptyContainer({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        border: `1px solid ${theme.colors.neutral20}`,
        borderRadius: theme.borderRadius.medium,
        height: 450,
        display: 'grid',
        alignContent: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  );
}

const Boundary = ({ error, resetError }: { error: unknown; resetError: () => void }) => {
  const { t } = useTranslation('base');

  if (!(error instanceof Error)) {
    return <Text weight='bold'>{t('Something went wrong.')}</Text>;
  }
  return (
    <div
      style={{
        display: 'grid',
        height: '100%',
        alignContent: 'center',
        justifyItems: 'center',
        gap: theme.spacing(2),
      }}
    >
      <Text weight='bold'>{t('Something went wrong.')}</Text>
      {/* This is waiting on the error boundary PR */}
      {/* <ErrorAccordion error={error} eventId={eventId} /> */}
      <Button onClick={resetError}>{t('Try again')}</Button>
    </div>
  );
};
